/**
 * @typedef {Object} UserData
 * @property {string} email
 * @property {string} first_name
 * @property {string} last_name
 * @property {Object} [metadata]
 * @property {string[]} [metadata.interest]
 */

/**
 * @typedef {Object} PurchasePayload
 * @property {number} value
 * @property {number} num_items
 * @property {string} [currency]
 * @property {string} [content_category]
 * @property {Object[]} [contents]
 */

/**
 * @typedef {Object} LoginPayload
 * @property {string} email
 * @property {string} [provider]
 */

/**
 * @typedef {Object} LevelSelectPayload
 * @property {string} level
 * @property {string} [tech]
 */

/**
 * @typedef {Object} PageViewPayload
 * @property {string} page
 */

/**
 * @typedef {Object} ToolConfig
 * @property {string|Function} [event]
 * @property {Function} transform
 * @property {Function} [customEvent]
 */

/**
 * @typedef {Object} EventConfig
 * @property {string} category
 * @property {Object.<string, ToolConfig>} tools
 */

/**
 * @typedef {Object} ToolHandler
 * @property {Function} track
 */

// Analytics Event Categories
export const AnalyticsCategory = {
  AUTHENTICATION: 'authentication',
  PURCHASE: 'purchase',
  USER_ACTION: 'user_action',
  PAGE_VIEW: 'page_view',
  MARKETING: 'marketing',
  UPSELL: 'upsell'
}

// Analytics Events Enum
export const AnalyticsEvent = {
  VOTE: 'vote',

  // Authentication Events
  LOGIN_EMAIL: 'login_email',
  LOGIN_SOCIAL: 'login_social',
  SIGNUP: 'signup',
  CREATE_ACCOUNT: 'create_account',

  // Purchase Events
  PURCHASE: 'purchase',
  INITIATE_CHECKOUT: 'initiate_checkout',

  // User Action Events
  VIEW_PRICING: 'view_pricing',
  SELECT_LEVEL_SENIOR: 'select_level_senior',
  SELECT_LEVEL_INTERMEDIATE: 'select_level_intermediate',
  SELECT_LEVEL_JUNIOR: 'select_level_junior',
  SELECT_LEVEL_INITIAL: 'select_level_initial',
  TOGGLE_PPP_PRICING: 'toggle_ppp_pricing',
  LINK_CLICK: 'track_link_click',
  BUY_NOW_CLICKED: 'buy_now_clicked',

  // Page View Events
  PAGE_VIEW: 'page_view',

  // Marketing Events
  LEAD_SIGNUP: 'lead_signup',
  LEAD_AUTH_SIGNUP: 'lead_auth_signup',

  // Upsell Events
  UPSELL_ITEM_ADDED: 'upsell_popup_item_added',
  UPSELL_POPUP_VIEWED: 'upsell_popup_viewed',
  UPSELL_POPUP_SUBMIT: 'upsell_popup_submit'
}

// Tracking Tools
export const TrackingTool = {
  MIXPANEL: '$mixpanel',
  FACEBOOK: '$facebook',
  GOOGLE_TAG_MANAGER: '$gtm',
  CUSTOMER_IO: '$cio',
  KISSMETRICS: '$kissmetrics',
  PLAUSIBLE: 'useTrackEvent',
  PARTNERO: 'partnero'
}

// Event Configuration
/** @type {Object.<string, EventConfig>} */
const eventConfig = {
  // Authentication Events
  [AnalyticsEvent.LOGIN_EMAIL]: {
    category: AnalyticsCategory.AUTHENTICATION,
    tools: {
      [TrackingTool.MIXPANEL]: {
        event: 'Login with email',
        transform: (/** @type {LoginPayload} */ payload) => payload
      },
      [TrackingTool.KISSMETRICS]: {
        event: 'login',
        transform: (payload) => ({
          via: 'email',
          email: payload.email
        })
      }
    }
  },
  [AnalyticsEvent.LOGIN_SOCIAL]: {
    category: AnalyticsCategory.AUTHENTICATION,
    tools: {
      [TrackingTool.MIXPANEL]: {
        event: (/** @type {LoginPayload} */ payload) => `Login with ${payload.provider}`,
        transform: (/** @type {LoginPayload} */ payload) => ({
          provider: payload.provider,
          email: payload.email
        })
      },
      [TrackingTool.KISSMETRICS]: {
        event: 'login',
        transform: (payload) => ({
          via: payload.provider,
          email: payload.email
        })
      }
    }
  },
  [AnalyticsEvent.SIGNUP]: {
    category: AnalyticsCategory.AUTHENTICATION,
    tools: {
      [TrackingTool.KISSMETRICS]: {
        transform: (payload) => ({
          via: payload.provider,
          email: payload.email,
          name: payload.name
        }),
        customEvent: ($kissmetrics, payload) => {
          $kissmetrics.track('signup_completed', {
            via: payload.provider,
            email: payload.email
          })

          $kissmetrics.setPeopleProperties({
            email: payload.email,
            name: payload.name
          })
        }
      },
      [TrackingTool.PARTNERO]: {
        event: 'signup',
        transform: (payload) => ({
          email: payload.email,
          name: payload.name
        })
      }
    }
  },
  [AnalyticsEvent.CREATE_ACCOUNT]: {
    category: AnalyticsCategory.AUTHENTICATION,
    tools: {
      [TrackingTool.MIXPANEL]: {
        event: 'Create account',
        transform: (/** @type {LoginPayload} */ payload) => payload
      },
      [TrackingTool.FACEBOOK]: {
        event: 'Lead',
        transform: () => ({ content_type: 'signup' })
      }
    }
  },

  // Purchase Events
  [AnalyticsEvent.PURCHASE]: {
    category: AnalyticsCategory.PURCHASE,
    tools: {
      [TrackingTool.MIXPANEL]: {
        event: 'Purchased',
        transform: () => ({})
      },
      [TrackingTool.FACEBOOK]: {
        event: 'Purchase',
        transform: (/** @type {PurchasePayload} */ payload) => ({
          content_category: payload.content_category || 'complete_purchase',
          currency: payload.currency || 'USD',
          value: payload.revenue,
          num_items: payload.num_items
        })
      },
      [TrackingTool.KISSMETRICS]: {
        customEvent: ($kissmetrics, payload) => {
          $kissmetrics.setPeopleProperties({
            subcriber: payload.subcriber,
            product_purchased: payload.product_purchased,
            revenue: payload.revenue
          })
          $kissmetrics.track('purchase', {
            tech: payload.tech,
            plan: payload.plan,
            discount: payload.discount,
            total_amount_charged: payload.total_amount_charged,
            amount_charged: payload.amount_charged
          })
        },
        transform: (payload) => payload
      }
    }
  },
  [AnalyticsEvent.INITIATE_CHECKOUT]: {
    category: AnalyticsCategory.PURCHASE,
    tools: {
      [TrackingTool.FACEBOOK]: {
        event: 'InitiateCheckout',
        transform: (/** @type {PurchasePayload} */ payload) => ({
          content_category: payload.content_category,
          num_items: payload.num_items,
          contents: payload.contents
        })
      }
    }
  },

  // User Action Events
  [AnalyticsEvent.BUY_NOW_CLICKED]: {
    category: AnalyticsCategory.USER_ACTION,
    tools: {
      [TrackingTool.KISSMETRICS]: {
        event: 'buy_now_clicked',
        transform: (payload) => ({
          tech: payload.tech,
          plan: payload.plan,
          user_category: payload.user_category,
          ppp: payload.ppp,
          ppp_country: payload.ppp_country,
          full_price: payload.full_price,
          effective_price: payload.effective_price,
          discount: payload.discount,
          is_presale: payload.is_presale
        })
      }
    }
  },
  [AnalyticsEvent.LINK_CLICK]: {
    category: AnalyticsCategory.USER_ACTION,
    tools: {
      [TrackingTool.KISSMETRICS]: {
        transform: (payload) => payload,
        customEvent: (/** @type {Object} */$kissmetrics, payload) => {
          $kissmetrics.trackLinkClick(payload)
        }
      }
    }
  },
  [AnalyticsEvent.VOTE]: {
    category: AnalyticsCategory.USER_ACTION,
    tools: {
      [TrackingTool.MIXPANEL]: {
        event: 'Cast Your Vote',
        transform: (payload) => null
      }
    }
  },

  [AnalyticsEvent.TOGGLE_PPP_PRICING]: {
    category: AnalyticsCategory.USER_ACTION,
    tools: {
      [TrackingTool.MIXPANEL]: {
        event: (payload) => `${payload.enabled ? 'Activate' : 'Deactivate'} PPP Pricing`,
        transform: (payload) => null
      }
    }
  },

  [AnalyticsEvent.VIEW_PRICING]: {
    category: AnalyticsCategory.USER_ACTION,
    tools: {
      [TrackingTool.MIXPANEL]: {
        event: 'View pricing',
        transform: (/** @type {Object} */ payload) => payload
      },
      [TrackingTool.KISSMETRICS]: {
        event: 'pricing_clicked',
        transform: (/** @type {Object} */ payload) => payload
      }
    }
  },
  [AnalyticsEvent.SELECT_LEVEL_SENIOR]: {
    category: AnalyticsCategory.USER_ACTION,
    tools: {
      [TrackingTool.MIXPANEL]: {
        event: 'Level senior developer select',
        transform: (/** @type {LevelSelectPayload} */ payload) => payload
      }
    }
  },
  [AnalyticsEvent.SELECT_LEVEL_INTERMEDIATE]: {
    category: AnalyticsCategory.USER_ACTION,
    tools: {
      [TrackingTool.MIXPANEL]: {
        event: 'Level intermediate developer select',
        transform: (/** @type {LevelSelectPayload} */ payload) => payload
      }
    }
  },
  [AnalyticsEvent.SELECT_LEVEL_JUNIOR]: {
    category: AnalyticsCategory.USER_ACTION,
    tools: {
      [TrackingTool.MIXPANEL]: {
        event: 'Level junior developer select',
        transform: (/** @type {LevelSelectPayload} */ payload) => payload
      }
    }
  },
  [AnalyticsEvent.SELECT_LEVEL_INITIAL]: {
    category: AnalyticsCategory.USER_ACTION,
    tools: {
      [TrackingTool.MIXPANEL]: {
        event: (/** @type {LevelSelectPayload} */ payload) => `Level ${payload.level} developer selected as an initial value ${payload.tech ? 'for ' + payload.tech : ''}`,
        transform: (/** @type {LevelSelectPayload} */ payload) => payload
      }
    }
  },

  [AnalyticsEvent.PAGE_VIEW]: {
    category: AnalyticsCategory.PAGE_VIEW,
    tools: {
      [TrackingTool.CUSTOMER_IO]: {
        event: 'Page View',
        transform: (/** @type {PageViewPayload} */ payload) => ({ page: payload.page })
      }
    }
  },

  // Marketing Events
  [AnalyticsEvent.LEAD_SIGNUP]: {
    category: AnalyticsCategory.MARKETING,
    tools: {
      [TrackingTool.FACEBOOK]: {
        event: 'Lead',
        transform: () => ({ content_type: 'signup' })
      },
      [TrackingTool.KISSMETRICS]: {
        event: 'signup_started',
        transform: () => ({ via: 'email' })
      }
    }
  },
  [AnalyticsEvent.LEAD_AUTH_SIGNUP]: {
    category: AnalyticsCategory.MARKETING,
    tools: {
      [TrackingTool.FACEBOOK]: {
        event: 'Lead',
        transform: () => ({ content_type: 'auth_signup' })
      },
      [TrackingTool.KISSMETRICS]: {
        event: 'signup_started',
        transform: (payload) => ({ via: payload.provider })
      }
    }
  },

  // Upsell Events
  [AnalyticsEvent.UPSELL_ITEM_ADDED]: {
    category: AnalyticsCategory.UPSELL,
    tools: {
      [TrackingTool.GOOGLE_TAG_MANAGER]: {
        event: 'upsell_popup_item_added',
        transform: (/** @type {Object} */ payload) => payload
      },
      [TrackingTool.PLAUSIBLE]: {
        event: 'upsell_popup_item_added',
        transform: (/** @type {Object} */ payload) => ({
          props: payload
        })
      }
    }
  },
  [AnalyticsEvent.UPSELL_POPUP_VIEWED]: {
    category: AnalyticsCategory.UPSELL,
    tools: {
      [TrackingTool.GOOGLE_TAG_MANAGER]: {
        event: 'upsell_popup_viewed',
        transform: (/** @type {Object} */ payload) => ({})
      },
      [TrackingTool.PLAUSIBLE]: {
        event: 'upsell_popup_viewed',
        transform: (/** @type {Object} */ payload) => ({
          props: {}
        })
      },
      [TrackingTool.KISSMETRICS]: {
        event: 'upsell_popup_viewed',
        transform: (payload) => ({
          tech: payload.tech,
          plan: payload.plan,
          user_category: payload.user_category,
          ppp: payload.ppp,
          ppp_country: payload.ppp_country,
          full_price: payload.full_price,
          effective_price: payload.effective_price,
          discount: payload.discount
        })
      }
    }
  },
  [AnalyticsEvent.UPSELL_POPUP_SUBMIT]: {
    category: AnalyticsCategory.UPSELL,
    tools: {
      [TrackingTool.GOOGLE_TAG_MANAGER]: {
        event: 'upsell_popup_submit',
        transform: (/** @type {Object} */ payload) => ({
          upsell_added: payload.upsell_added
        })
      },
      [TrackingTool.PLAUSIBLE]: {
        event: 'upsell_popup_submit',
        transform: (/** @type {Object} */ payload) => ({
          props: {
            upsell_added: payload.upsell_added
          }
        })
      },
      [TrackingTool.KISSMETRICS]: {
        event: 'upsell_popup_submit',
        transform: (payload) => ({
          tech: payload.tech,
          plan: payload.plan,
          user_category: payload.user_category,
          ppp: payload.ppp,
          ppp_country: payload.ppp_country,
          full_price: payload.full_price,
          effective_price: payload.effective_price,
          discount: payload.discount,
          product_added: payload.product_added,
          new_price: payload.new_price,
          upsell_discount: payload.upsell_discount,
          applied: payload.applied
        })
      }
    }
  }
}

// Tool Handlers
/** @type {Object.<string, ToolHandler>} */
const toolHandlers = {
  [TrackingTool.MIXPANEL]: {
    track: ($mixpanel, event, payload) => {
      $mixpanel?.track(event, payload)
    }
  },
  [TrackingTool.FACEBOOK]: {
    track: ($fbq, event, payload) => {
      $fbq?.('track', event, payload)
    }
  },
  [TrackingTool.GOOGLE_TAG_MANAGER]: {
    track: ($gtm, event, payload) => {
      $gtm?.track({ event, ...payload })
    }
  },
  [TrackingTool.CUSTOMER_IO]: {
    track: ($cio, event, payload) => {
      $cio?.track?.(event, payload)
    }
  },
  [TrackingTool.PLAUSIBLE]: {
    track: (useTrackEvent, event, payload) => {
      useTrackEvent(event, payload || null)
    }
  },
  [TrackingTool.KISSMETRICS]: {
    track: ($kissmetrics, event, payload) => {
      $kissmetrics.track(event, payload || null)
    }
  },
  [TrackingTool.PARTNERO]: {
    track: (partnero, event, payload) => {
      if (typeof partnero?.[event] === 'function') {
        partnero[event](payload)
      }
    }
  }
}

/**
 * @returns {{
 *   track: (event: string, payload?: Object) => void,
 *   identifyUser: (userData: UserData) => void,
 *   addTool: (tool: string, handler: ToolHandler) => void,
 *   addEvent: (event: string, config: EventConfig) => void,
 *   AnalyticsEvent: AnalyticsEvent,
 *   AnalyticsCategory: Object,
 *   TrackingTool: Object
 * }}
 */
export const useAnalytics = () => {
  const { $mixpanel, $gtm, $cio, $fbq, $kissmetrics } = useNuxtApp()
  const partnero = usePartnero()
  const runtimeConfig = useRuntimeConfig()

  /**
   * @param {string} event
   * @param {Object} [payload={}]
   */
  const track = (event, payload = {}) => {
    const config = eventConfig[event]
    if (!config) {
      console.warn(`No configuration found for event: ${event}`)
      return
    }

    Object.entries(config.tools).forEach(([tool, toolConfig]) => {
      const handler = toolHandlers[tool]
      if (!handler) {
        console.warn(`No handler found for tool: ${tool}`)
        return
      }

      const eventName = typeof toolConfig.event === 'function'
        ? toolConfig.event(payload)
        : toolConfig.event

      const transformedPayload = toolConfig.transform(payload)
      const toolsMap = { $mixpanel, $gtm, $cio, $fbq, $kissmetrics, useTrackEvent, partnero }

      if (!toolConfig.event && toolConfig.customEvent) {
        if (runtimeConfig.public.ENVIRONMENT_NAME !== 'production') {
          console.log(`▶️ Tracked Event - ${eventName}`, {
            tool,
            eventName,
            payload: transformedPayload
          })
        }

        toolConfig.customEvent(toolsMap[tool], transformedPayload)
        return
      } else if (!toolConfig.customEvent) {
        console.error(`No event handler for ${tool} - ${eventName}`)
        return
      }

      if (runtimeConfig.public.ENVIRONMENT_NAME !== 'production') {
        if (!toolsMap[tool]) {
          console.warn(`No tool found for: ${tool}`)
          return
        }
        console.log(`▶️ Tracked Event - ${eventName}`, {
          tool,
          eventName,
          payload: transformedPayload
        })
      }

      handler.track(toolsMap[tool], eventName, transformedPayload)
    })
  }

  /**
   * @param {UserData} userData
   */
  const identifyUser = (userData) => {
    $cio?.identify(userData.id, {
      id: userData.email,
      created_at: Math.floor(new Date().getTime() / 1000),
      registered_from: userData.metadata?.interest?.[0],
      first_name: userData.first_name,
      last_name: userData.last_name,
      email: userData.email,
      interest: userData.metadata?.interest,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    })
  }

  /**
   * @param {string} tool
   * @param {ToolHandler} handler
   */
  const addTool = (tool, handler) => {
    toolHandlers[tool] = handler
  }

  /**
   * @param {string} event
   * @param {EventConfig} config
   */
  const addEvent = (event, config) => {
    eventConfig[event] = config
  }

  return {
    track,
    identifyUser,
    addTool,
    addEvent,
    AnalyticsEvent,
    AnalyticsCategory,
    TrackingTool
  }
}
